<script setup lang="ts">
/**
 * Карточка шага онбординга: заголовок, крестик, текст, прогресс точками и кнопки «Назад» / «Далее».
 */
const props = defineProps<{
  title: string
  text: string
  position: number
  total: number
  isLoading: boolean
}>()

const emit = defineEmits<{
  prev: []
  next: []
  close: []
}>()

const nextButton = ref<{ $el: HTMLElement } | null>(null)

const isFirst = computed(() => props.position <= 1)
const isLast = computed(() => props.position >= props.total)

/** Фокус на «Далее» при смене шага, чтобы Enter и стрелки работали сразу */
watch(() => props.title, async () => {
  await nextTick()
  nextButton.value?.$el?.focus()
}, { immediate: true })
</script>

<template>
  <VCard
    class="onboarding-card"
    elevation="12"
    rounded="lg"
    role="dialog"
    aria-modal="true"
    :aria-label="title"
  >
    <div class="d-flex align-center ga-2 pa-4 pb-0">
      <div class="text-h6 flex-grow-1">
        {{ title }}
      </div>
      <IconBtn
        size="small"
        class="onboarding-card__close"
        aria-label="Закрыть обучение"
        @click="emit('close')"
      >
        <VIcon icon="ri-close-line" />
      </IconBtn>
    </div>

    <div class="onboarding-card__text text-body-2 text-medium-emphasis px-4 pt-2">
      {{ text }}
    </div>

    <!-- В сценарии десятки шагов — точки не помещаются, поэтому прогресс полосой -->
    <div
      v-if="total > 1"
      class="onboarding-card__progress mx-4 mt-4"
      aria-hidden="true"
    >
      <div
        class="onboarding-card__progress-value"
        :style="{ inlineSize: `${Math.min(100, (position / total) * 100)}%` }"
      />
    </div>

    <div class="d-flex ga-3 pa-4">
      <VBtn
        v-if="!isFirst"
        class="flex-grow-1"
        variant="outlined"
        color="secondary"
        :disabled="isLoading"
        @click="emit('prev')"
      >
        Назад
      </VBtn>
      <VBtn
        ref="nextButton"
        class="flex-grow-1"
        color="primary"
        :loading="isLoading"
        @click="emit('next')"
      >
        {{ isLast ? 'Готово' : 'Далее' }}
      </VBtn>
    </div>
  </VCard>
</template>

<style scoped lang="scss">
.onboarding-card {
  background: rgb(var(--v-theme-surface));
}

.onboarding-card__close {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.onboarding-card__text {
  white-space: pre-line;
}

.onboarding-card__progress {
  overflow: hidden;
  block-size: 4px;
  border-radius: 2px;
  background: rgba(var(--v-theme-on-surface), var(--v-border-opacity));
}

.onboarding-card__progress-value {
  block-size: 100%;
  border-radius: 2px;
  background: rgb(var(--v-theme-primary));
  transition: inline-size 0.3s ease;
}

@media (prefers-reduced-motion: reduce) {
  .onboarding-card__progress-value {
    transition: none;
  }
}
</style>
